import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  FileText, Calendar, Settings2, Layers, MapPin, MessageSquare,
  ChevronRight, Loader2, Baby, Syringe, Send,
  Wand2, RefreshCw, X, Sparkles, CheckCircle2, FileDown, History, Clock,
  AlertTriangle,
} from "lucide-react";
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, ImageRun } from "docx";
import PptxGenJS from "pptxgenjs";
import { saveAs } from "file-saver";
import { toPng } from "html-to-image";
import { Button } from "@/components/ui/Button";
import { Navbar } from "@/components/layout/Navbar";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/i18n/LanguageContext";
import { getAuthToken } from "@/lib/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { SidebarPanel } from "./components/SidebarPanel";
import { SectionChart } from "./components/SectionChart";
import {
  SAMPLE_REPORT_META, SAMPLE_REPORT_SECTIONS, REGIONS,
  QUARTERS, MONTHS, MONTHS_AMHARIC, MONTH_GREGORIAN_EQUIVALENTS, YEARS,
  PERCENT_LABEL_REGEX, NUMBER_PREFIX_REGEX, PPTX_THEME,
} from "./constants";
import {
  bindReportHeader,
  formatEthiopianYear,
  reportRequestFromMeta,
  tryBuildReportRequest,
} from "./reportRequest";
import {
  cleanSectionBody,
  cleanSectionContent,
  stripLeadingDuplicateTitle,
} from "./reportContent";
import type {
  TemplateType, ReportSection, ReportMeta, ReportTableRow,
  CachedReportSummary, ReportGeneratorPageProps, SidebarPanelKey,
  ReportRequest, ReportRequestError,
} from "./types";

export const ReportGeneratorPage: React.FC<ReportGeneratorPageProps> = ({
  publicMode = false,
  embedMode = false,
}) => {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "";
  // State Management
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType>("yearly");
  const [year, setYear] = useState(YEARS[0]);
  const [quarter, setQuarter] = useState(QUARTERS[0]);
  const [month, setMonth] = useState(MONTHS[0]);
  const [regionType, setRegionType] = useState<"national" | "regional">("national");
  const [selectedRegion, setSelectedRegion] = useState("Oromia");
  const [userInstruction, setUserInstruction] = useState(
    "Generate a concise health performance report using ...",
  );
  const [reportLanguage, setReportLanguage] = useState<"en" | "am">("en");
  const [exportFormat, setExportFormat] = useState<"docx" | "pptx">("docx");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [reportData, setReportData] = useState<ReportSection[] | null>(null);
  const [reportMeta, setReportMeta] = useState<ReportMeta | null>(null);
  const [activeRequest, setActiveRequest] = useState<ReportRequest | null>(null);
  const [activeRequestUnavailable, setActiveRequestUnavailable] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [recentReports, setRecentReports] = useState<CachedReportSummary[]>([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [activeRecentId, setActiveRecentId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarPanels, setSidebarPanels] = useState<Record<SidebarPanelKey, boolean>>({
    template: false,
    parameters: false,
    geography: false,
  });

  useEffect(() => {
    if (!location.pathname.includes("report-generator")) {
      setImprovingSectionId(null);
      setIsApplyingImprovement(false);
    }
  }, [location.pathname]);

  const toggleSidebarPanel = (key: SidebarPanelKey) => {
    setSidebarPanels((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isPercentLabel = (label?: string): boolean =>
    typeof label === "string" && PERCENT_LABEL_REGEX.test(label);

  const clampPercentValue = (value: number): number =>
    Number.isFinite(value) ? Math.min(100, value) : value;

  const normalizeChartData = (raw: any): { name: string; value: number }[] | undefined => {
    if (!raw) return undefined;
    if (Array.isArray(raw)) {
      return raw
        .map((item) => ({
          name: String(item?.name ?? item?.label ?? ""),
          value: Number(item?.value ?? item?.data ?? 0),
        }))
        .filter((item) => item.name && Number.isFinite(item.value));
    }
    if (raw.labels && raw.datasets && raw.datasets[0]?.data) {
      const labels = raw.labels as string[];
      const values = raw.datasets[0].data as number[];
      return labels
        .map((label, idx) => ({
          name: String(label),
          value: Number(values[idx] ?? 0),
        }))
        .filter((item) => item.name && Number.isFinite(item.value));
    }
    return undefined;
  };

  const normalizeChartMetricLabel = (raw: any): string | undefined => {
    if (!raw) return undefined;
    const label = raw?.datasets?.[0]?.label ?? raw?.value_key;
    return typeof label === "string" && label.trim() ? label : undefined;
  };

  const normalizeTableData = (raw: any): ReportTableRow[] | undefined => {
    if (!Array.isArray(raw)) return undefined;
    const shouldClampKey = (key: string): boolean =>
      PERCENT_LABEL_REGEX.test(key);

    return raw
      .filter((item): item is Record<string, unknown> => !!item && typeof item === "object" && !Array.isArray(item))
      .map((item) =>
        Object.fromEntries(
          Object.entries(item).map(([key, value]) => {
            const normalizedValue =
              typeof value === "number"
                ? shouldClampKey(key)
                  ? clampPercentValue(value)
                  : value
                : typeof value === "string" || typeof value === "boolean" || value === null
                ? value
                : String(value);

            return [key, normalizedValue];
          })
        )
      );
  };

  const convertToChartJSFormat = (normalizedData: { name: string; value: number }[] | undefined, metricLabel?: string): any => {
    if (!normalizedData || normalizedData.length === 0) return undefined;

    const clampValues = isPercentLabel(metricLabel);
    return {
      labels: normalizedData.map(item => item.name),
      datasets: [{
        label: metricLabel || "Value",
        data: normalizedData.map((item) => clampValues ? clampPercentValue(item.value) : item.value),
      }],
      value_key: metricLabel || "Value",
    };
  };

  const stripGeneratedNumberPrefix = (title: string): string => {
    let result = title;
    for (const re of NUMBER_PREFIX_REGEX) {
      result = result.replace(re, "");
    }
    return result.trim();
  };

  const normalizeReportMeta = (template: any): ReportMeta => ({
    title: typeof template?.title === "string" ? template.title : undefined,
    reportType: typeof template?.report_type === "string" ? template.report_type : undefined,
    geography: typeof template?.geography === "string" ? template.geography : undefined,
    timePeriod: typeof template?.time_period === "string" ? template.time_period : undefined,
  });

  const buildReportSection = (
    section: any,
    existing?: ReportSection,
    overrides: Partial<ReportSection> = {}
  ): ReportSection => {
    const id = String(section?.section_id || section?.section_title || existing?.id || "");

    return {
      id,
      title: stripGeneratedNumberPrefix(String(section?.section_title || existing?.title || id)),
      content:
        overrides.content ??
        (typeof section?.section_description === "string"
          ? section.section_description
          : existing?.content ?? ""),
      chartType: (section?.chart_type ?? existing?.chartType) as ReportSection["chartType"],
      chartData: normalizeChartData(section?.chart_data) ?? existing?.chartData,
      chartMetricLabel: normalizeChartMetricLabel(section?.chart_data) ?? existing?.chartMetricLabel,
      tableData: normalizeTableData(section?.table_data) ?? existing?.tableData,
      isUpdating: existing?.isUpdating,
      isStreaming: overrides.isStreaming ?? existing?.isStreaming ?? false,
      chartRef: existing?.chartRef ?? React.createRef<HTMLDivElement>(),
    };
  };

  const shouldHideDuplicateReportTitleSection = (sectionTitle: string, index: number): boolean => {
    const normalizedSectionTitle = sectionTitle.trim().toLowerCase();
    const normalizedReportTitle = displayReportTitle.trim().toLowerCase();
    return index === 0 && normalizedSectionTitle === normalizedReportTitle;
  };

  const handleExportDocx = async () => {
    if (!reportData || !headerBinding) return;
    setIsExporting(true);

    try {
      const children: any[] = [
        new Paragraph({
          text: displayReportTitle,
          heading: HeadingLevel.TITLE,
          alignment: AlignmentType.CENTER,
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: `${reportCopy.period}: ${displayPeriodText}`,
              bold: true,
            }),
          ],
          alignment: AlignmentType.CENTER,
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: `${reportCopy.geography}: ${displayGeographyText}`,
              bold: true,
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        }),
      ];

      for (const [idx, section] of reportData.entries()) {
        if (!shouldHideDuplicateReportTitleSection(section.title ?? "", idx)) {
          const sectionNumber = reportData.slice(0, idx + 1).filter((item, itemIdx) => !shouldHideDuplicateReportTitleSection(item.title ?? "", itemIdx)).length;
          children.push(
            new Paragraph({
              text: `${sectionNumber}. ${section.title}`,
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 400, after: 200 },
            })
          );
        }

        if (section.chartRef?.current) {
          try {
            const dataUrl = await toPng(section.chartRef.current, { 
              pixelRatio: 2,
              backgroundColor: '#ffffff'
            });
            const base64Data = dataUrl.split(',')[1];
            
            children.push(
              new Paragraph({
                children: [
                  new ImageRun({
                    data: Uint8Array.from(atob(base64Data), c => c.charCodeAt(0)),
                    transformation: {
                      width: 600,
                      height: 300,
                    },
                    type: "png", 
                  }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { before: 200, after: 200 },
              })
            );
          } catch (err) {
            console.error(`Failed to capture chart for section ${section.title}:`, err);
          }
        }

        children.push(
          new Paragraph({
            text: cleanSectionBody(section.content, section.title),
            spacing: { after: 200 },
          })
        );
      }

      const doc = new Document({
        sections: [
          {
            properties: {},
            children: children,
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `Health_Report_${year}_${regionType}.docx`);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPptx = async () => {
    if (!reportData || !headerBinding) return;
    setIsExporting(true);

    try {
      const pptx = new PptxGenJS();
      pptx.layout = "LAYOUT_WIDE";
      pptx.author = "Healthcare Data Analytics Agent";
      pptx.subject = displayReportTitle;
      pptx.title = displayReportTitle;
      pptx.company = "Ministry of Health Ethiopia";
      pptx.theme = {
        headFontFace: "Aptos Display",
        bodyFontFace: "Aptos",
      };

      const theme = PPTX_THEME;

      const visibleSections = reportData.filter(
        (section, idx) => !shouldHideDuplicateReportTitleSection(section.title ?? "", idx)
      );

      const cleanText = (value: string): string => cleanSectionContent(value);

      const toBullets = (value: string, maxItems = 5, title = ""): string[] => {
        const source = title ? stripLeadingDuplicateTitle(value, title) : value;
        const normalized = cleanText(source);
        if (!normalized) return [];
        const lineParts = source
          .split(/\r?\n/)
          .map((line) => cleanText(line.replace(/^[-*•]\s*/, "")))
          .filter((line) => line && !/^[-:]+$/.test(line) && !line.includes("---"));        const candidates = lineParts.length >= 2
          ? lineParts
          : normalized.split(/(?<=[.!?።])\s+/).map((item) => item.trim());

        return candidates
          .filter(Boolean)
          .slice(0, maxItems)
          .map((item) => (item.length > 175 ? `${item.slice(0, 172).trim()}...` : item));
      };

      const slideFooter = (slide: any, page: number) => {
        slide.addShape(pptx.ShapeType.line, {
          x: 0.55,
          y: 7.04,
          w: 12.2,
          h: 0,
          line: { color: theme.faint, width: 1 },
        });
        slide.addText("Integrated Health Data System", {
          x: 0.58,
          y: 7.12,
          w: 4.3,
          h: 0.18,
          fontSize: 7.5,
          color: theme.muted,
          margin: 0,
        });
        slide.addText(String(page).padStart(2, "0"), {
          x: 12.1,
          y: 7.08,
          w: 0.65,
          h: 0.22,
          fontSize: 8,
          bold: true,
          color: theme.cyanDark,
          align: "right",
          margin: 0,
        });
      };

      const addKicker = (slide: any, text: string) => {
        slide.addShape(pptx.ShapeType.line, {
          x: 0.6,
          y: 0.46,
          w: 0.42,
          h: 0,
          line: { color: theme.cyan, width: 2 },
        });
        slide.addText(text.toUpperCase(), {
          x: 1.08,
          y: 0.33,
          w: 5.2,
          h: 0.26,
          fontSize: 8,
          bold: true,
          color: theme.cyanDark,
          charSpace: 1.2,
          margin: 0,
        });
      };

      const addMetadataPills = (slide: any, x: number, y: number) => {
        const pills = [
          `${reportCopy.period}: ${displayPeriodText}`,
          `${reportCopy.geography}: ${displayGeographyText}`,
          exportFormat.toUpperCase(),
        ];
        let cursor = x;
        pills.forEach((pill, index) => {
          const width = index === 2 ? 0.7 : Math.min(3.8, Math.max(1.8, pill.length * 0.075));
          slide.addShape(pptx.ShapeType.roundRect, {
            x: cursor,
            y,
            w: width,
            h: 0.32,
            rectRadius: 0.05,
            fill: { color: index === 2 ? theme.cyan : theme.panel },
            line: { color: index === 2 ? theme.cyan : theme.faint, width: 0.7 },
          });
          slide.addText(pill, {
            x: cursor + 0.09,
            y: y + 0.08,
            w: width - 0.18,
            h: 0.13,
            fontSize: 7.5,
            bold: index === 2,
            color: index === 2 ? theme.white : theme.muted,
            align: "center",
            margin: 0,
            fit: "shrink",
          });
          cursor += width + 0.12;
        });
      };

      const captureChart = async (section: ReportSection): Promise<string | null> => {
        if (!section.chartRef?.current) return null;
        try {
          return await toPng(section.chartRef.current, {
            pixelRatio: 2,
            backgroundColor: "#ffffff",
          });
        } catch (err) {
          console.error(`Failed to capture chart for section ${section.title}:`, err);
          return null;
        }
      };

      const addBulletList = (slide: any, bullets: string[], x: number, y: number, w: number, h: number) => {
        const lines = bullets.length ? bullets : ["No narrative text was generated for this section."];
        slide.addText(
          lines.map((bullet) => ({
            text: bullet,
            options: { bullet: { indent: 12 }, hanging: 3, breakLine: true },
          })),
          {
            x,
            y,
            w,
            h,
            fontSize: 12,
            color: theme.ink,
            breakLine: false,
            fit: "shrink",
            margin: 0.02,
            paraSpaceAfterPt: 8,
            valign: "top",
          }
        );
      };

      const addDataSnapshot = (slide: any, section: ReportSection, x: number, y: number, w: number) => {
        const rows = (section.tableData ?? []).slice(0, 4);
        if (!rows.length) return;
        const columns = Object.keys(rows[0]).slice(0, 3);
        if (!columns.length) return;

        slide.addText(reportLanguage === "am" ? "የውሂብ ቅኝት" : "Data snapshot", {
          x,
          y,
          w,
          h: 0.22,
          fontSize: 8,
          bold: true,
          color: theme.cyanDark,
          charSpace: 0.7,
          margin: 0,
        });

        const rowH = 0.34;
        const colW = w / columns.length;
        columns.forEach((column, colIdx) => {
          slide.addShape(pptx.ShapeType.rect, {
            x: x + colIdx * colW,
            y: y + 0.32,
            w: colW,
            h: rowH,
            fill: { color: theme.cyanDark },
            line: { color: theme.cyanDark, width: 0.3 },
          });
          slide.addText(column.replace(/_/g, " "), {
            x: x + colIdx * colW + 0.05,
            y: y + 0.42,
            w: colW - 0.1,
            h: 0.12,
            fontSize: 6.5,
            bold: true,
            color: theme.white,
            margin: 0,
            fit: "shrink",
          });
        });

        rows.forEach((row, rowIdx) => {
          columns.forEach((column, colIdx) => {
            const top = y + 0.32 + (rowIdx + 1) * rowH;
            slide.addShape(pptx.ShapeType.rect, {
              x: x + colIdx * colW,
              y: top,
              w: colW,
              h: rowH,
              fill: { color: rowIdx % 2 === 0 ? "FFFFFF" : "F8FAFC" },
              line: { color: theme.faint, width: 0.3 },
            });
            slide.addText(String(row[column] ?? ""), {
              x: x + colIdx * colW + 0.05,
              y: top + 0.09,
              w: colW - 0.1,
              h: 0.12,
              fontSize: 6.5,
              color: theme.ink,
              margin: 0,
              fit: "shrink",
            });
          });
        });
      };

      let page = 1;
      const titleSlide = pptx.addSlide();
      titleSlide.background = { color: "020617" };
      titleSlide.addShape(pptx.ShapeType.rect, {
        x: 7.55,
        y: 0,
        w: 13.33,
        h: 7.5,
        fill: { color: "020617" },
        line: { color: "020617", transparency: 100 },
      });
      titleSlide.addShape(pptx.ShapeType.rect, {
        x: 0,
        y: 0,
        w: 5.78,
        h: 7.5,
        fill: { color: "155E75", transparency: 10 },
        line: { color: "155E75", transparency: 100 },
      });
      addKicker(titleSlide, reportLanguage === "am" ? "የጤና አፈጻጸም ሪፖርት" : "Health performance report");
      titleSlide.addShape(pptx.ShapeType.rect, {
        x: 0.62,
        y: 0.82,
        w: 0.7,
        h: 0.05,
        fill: { color: "67E8F9" },
        line: { color: "67E8F9" },
      });
      titleSlide.addText(reportLanguage === "am" ? "á‹¨áŒ¤áŠ“ áŠ áˆáŒ»áŒ¸áˆ áˆªá–áˆ­á‰µ" : "Health Performance Deck", {
        x: 0.62,
        y: 1.05,
        w: 5.4,
        h: 0.22,
        fontSize: 8,
        bold: true,
        color: "CFFAFE",
        margin: 0,
      });
      titleSlide.addText(displayReportTitle, {
        x: 0.6,
        y: 1.62,
        w: 9.0,
        h: 1.65,
        fontSize: 31,
        bold: true,
        color: theme.white,
        margin: 0,
        fit: "shrink",
        breakLine: false,
      });
      titleSlide.addText(
        reportLanguage === "am"
          ? "ለውሳኔ ሰጪዎች የተዘጋጀ የመረጃ ተኮር ማቅረቢያ"
          : "A data-grounded presentation prepared for health program review",
        {
          x: 0.62,
          y: 2.75,
          w: 6.3,
          h: 0.34,
          fontSize: 12,
          color: theme.muted,
          margin: 0,
        }
      );
      addMetadataPills(titleSlide, 0.62, 3.28);
      titleSlide.addShape(pptx.ShapeType.rect, {
        x: 8.45,
        y: 1.02,
        w: 4.25,
        h: 4.65,
        fill: { color: theme.white },
        line: { color: "BDECF4", width: 1 },
        shadow: { type: "outer", color: "D9EEF3", opacity: 0.35, blur: 1, angle: 45 },
      });
      titleSlide.addText(String(visibleSections.length), {
        x: 9.08,
        y: 1.7,
        w: 1.5,
        h: 0.62,
        fontSize: 34,
        bold: true,
        color: theme.cyanDark,
        margin: 0,
      });
      titleSlide.addText(reportLanguage === "am" ? "የሪፖርት ክፍሎች" : "report sections", {
        x: 10.45,
        y: 1.92,
        w: 1.6,
        h: 0.22,
        fontSize: 9,
        bold: true,
        color: theme.muted,
        margin: 0,
      });
      titleSlide.addText(
        visibleSections.slice(0, 5).map((section) => `• ${section.title}`).join("\n"),
        {
          x: 9.08,
          y: 2.78,
          w: 2.95,
          h: 1.95,
          fontSize: 10,
          color: theme.ink,
          breakLine: false,
          fit: "shrink",
          margin: 0,
          valign: "top",
        }
      );
      slideFooter(titleSlide, page++);

      const overviewSlide = pptx.addSlide();
      overviewSlide.background = { color: theme.white };
      addKicker(overviewSlide, reportLanguage === "am" ? "አጠቃላይ እይታ" : "Overview");
      overviewSlide.addText(
        reportLanguage === "am" ? "የማቅረቢያው አወቃቀር" : "What this deck covers",
        {
          x: 0.6,
          y: 0.78,
          w: 6.7,
          h: 0.42,
          fontSize: 22,
          bold: true,
          color: theme.ink,
          margin: 0,
        }
      );
      addMetadataPills(overviewSlide, 8.1, 0.78);
      visibleSections.slice(0, 8).forEach((section, idx) => {
        const col = idx % 2;
        const row = Math.floor(idx / 2);
        const x = 0.78 + col * 6.05;
        const y = 1.6 + row * 1.14;
        overviewSlide.addShape(pptx.ShapeType.rect, {
          x,
          y,
          w: 5.35,
          h: 0.82,
          fill: { color: idx % 2 === 0 ? "F8FAFC" : "ECFEFF" },
          line: { color: idx % 2 === 0 ? theme.faint : "BAE6FD", width: 0.7 },
        });
        overviewSlide.addText(String(idx + 1).padStart(2, "0"), {
          x: x + 0.18,
          y: y + 0.19,
          w: 0.52,
          h: 0.2,
          fontSize: 11,
          bold: true,
          color: theme.cyanDark,
          margin: 0,
        });
        overviewSlide.addText(section.title, {
          x: x + 0.78,
          y: y + 0.17,
          w: 4.2,
          h: 0.34,
          fontSize: 10.5,
          bold: true,
          color: theme.ink,
          fit: "shrink",
          margin: 0,
        });
        const preview = toBullets(section.content, 1, section.title)[0] ?? "";
        overviewSlide.addText(preview, {
          x: x + 0.78,
          y: y + 0.48,
          w: 4.2,
          h: 0.16,
          fontSize: 7.5,
          color: theme.muted,
          fit: "shrink",
          margin: 0,
        });
      });
      slideFooter(overviewSlide, page++);

      for (const [visibleIdx, section] of visibleSections.entries()) {
        const slide = pptx.addSlide();
        slide.background = { color: theme.white };
        addKicker(slide, `${reportLanguage === "am" ? "ክፍል" : "Section"} ${visibleIdx + 1}`);
        slide.addText(section.title, {
          x: 0.6,
          y: 0.75,
          w: 7.3,
          h: 0.58,
          fontSize: 20,
          bold: true,
          color: theme.ink,
          fit: "shrink",
          margin: 0,
        });

        const bullets = toBullets(section.content, section.chartType ? 4 : 6, section.title);
        const chartDataUrl = await captureChart(section);

        if (chartDataUrl) {
          slide.addShape(pptx.ShapeType.rect, {
            x: 0.62,
            y: 1.58,
            w: 7.15,
            h: 4.62,
            fill: { color: theme.white },
            line: { color: theme.faint, width: 0.8 },
            shadow: { type: "outer", color: "E2E8F0", opacity: 0.3, blur: 1, angle: 45 },
          });
          slide.addImage({
            data: chartDataUrl,
            x: 0.82,
            y: 1.78,
            w: 6.75,
            h: 3.95,
          });
          slide.addText(section.chartMetricLabel ?? (reportLanguage === "am" ? "ምስላዊ መረጃ" : "Visualization"), {
            x: 0.84,
            y: 5.83,
            w: 5.2,
            h: 0.16,
            fontSize: 7.2,
            color: theme.muted,
            margin: 0,
          });

          slide.addShape(pptx.ShapeType.rect, {
            x: 8.05,
            y: 1.58,
            w: 4.72,
            h: 4.62,
            fill: { color: theme.panel },
            line: { color: "D8F3F7", width: 0.8 },
          });
          slide.addText(reportLanguage === "am" ? "ዋና ምልከታዎች" : "Key takeaways", {
            x: 8.35,
            y: 1.9,
            w: 3.9,
            h: 0.22,
            fontSize: 9,
            bold: true,
            color: theme.cyanDark,
            margin: 0,
          });
          addBulletList(slide, bullets, 8.35, 2.32, 3.88, 3.35);
        } else {
          slide.addShape(pptx.ShapeType.rect, {
            x: 0.62,
            y: 1.58,
            w: 7.15,
            h: 4.76,
            fill: { color: theme.panel },
            line: { color: "D8F3F7", width: 0.8 },
          });
          addBulletList(slide, bullets, 0.95, 1.95, 6.45, 3.3);

          slide.addShape(pptx.ShapeType.rect, {
            x: 8.05,
            y: 1.58,
            w: 4.72,
            h: 4.76,
            fill: { color: theme.white },
            line: { color: theme.faint, width: 0.8 },
          });
          if (false) {
          addDataSnapshot(slide, section, 8.35, 1.96, 3.85);
          if (!section.tableData?.length) {
            slide.addText(reportLanguage === "am" ? "ተጨማሪ የሰንጠረዥ ውሂብ አልተገኘም።" : "No additional table data was supplied for this section.", {
              x: 8.45,
              y: 2.25,
              w: 3.6,
              h: 0.5,
              fontSize: 10,
              color: theme.muted,
              fit: "shrink",
              margin: 0,
            });
          }
          }
        }

        slideFooter(slide, page++);
      }

      if (false) {
      const closingSlide = pptx.addSlide();
      closingSlide.background = { color: "F7FBFC" };
      addKicker(closingSlide, reportLanguage === "am" ? "መዝጊያ" : "Close");
      closingSlide.addText(reportLanguage === "am" ? "ሪፖርቱ ለመወሰን ዝግጁ ነው" : "Report is ready for review and action", {
        x: 0.68,
        y: 1.1,
        w: 7.6,
        h: 0.64,
        fontSize: 25,
        bold: true,
        color: theme.ink,
        margin: 0,
        fit: "shrink",
      });
      closingSlide.addText(
        reportLanguage === "am"
          ? "የሚቀጥለው ደረጃ፡ የክፍሎችን ግኝት ይገምግሙ፣ የቅድሚያ ድርጊቶችን ይለዩ፣ እና የክትትል መለኪያዎችን ያስቀምጡ።"
          : "Next step: review section findings, identify priority actions, and confirm follow-up metrics.",
        {
          x: 0.72,
          y: 2.05,
          w: 6.9,
          h: 0.72,
          fontSize: 14,
          color: theme.muted,
          margin: 0,
          fit: "shrink",
        }
      );
      addMetadataPills(closingSlide, 0.72, 3.1);
      slideFooter(closingSlide, page++);
      }

      const blob = await pptx.write({ outputType: "blob" });
      const safeTitle = displayReportTitle
        .replace(/[^\w\s-]/g, "")
        .trim()
        .replace(/\s+/g, "_")
        .slice(0, 60) || "Health_Report";
      saveAs(blob as Blob, `${safeTitle}_${year}_${regionType}.pptx`);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadReport = () => {
    if (exportFormat === "pptx") {
      void handleExportPptx();
      return;
    }
    void handleExportDocx();
  };

  // Improve Panel State
  const [improvingSectionId, setImprovingSectionId] = useState<string | null>(null);
  const [improveInstruction, setImproveInstruction] = useState("");
  const [isApplyingImprovement, setIsApplyingImprovement] = useState(false);
  const [improvementError, setImprovementError] = useState<string | null>(null);

  const templates = [
    { id: "yearly", label: "MoH Yearly", icon: Calendar },
    { id: "quarterly", label: "MoH Quarterly", icon: Layers },
    { id: "monthly", label: "MoH Monthly", icon: FileText },
  ];

  const regions = REGIONS;
  const quarters = QUARTERS;
  const months = MONTHS;
  // The data source records Ethiopian month names, so periods are selected in that
  // calendar and annotated with the Gregorian equivalent users also recognise.
  const monthLabel = (monthName: string): string => {
    const amharic = MONTHS_AMHARIC[monthName];
    const gregorian = MONTH_GREGORIAN_EQUIVALENTS[monthName];
    const local = reportLanguage === "am" && amharic ? amharic : monthName;
    return gregorian ? `${local} (${gregorian})` : local;
  };
  const years = YEARS;
  const nationalGeographyLabel = t("reports.sidebar.nationalLevel");
  const isSamplePreview = !reportData && !isGenerating;
  const previewSections = reportData ?? (isSamplePreview ? SAMPLE_REPORT_SECTIONS : null);
  const previewMeta = reportMeta ?? (isSamplePreview ? SAMPLE_REPORT_META : null);

  // The sidebar controls are the draft request. It is resolved on every change so an
  // unavailable period is reported before a generation is even attempted.
  const draftResolution = useMemo(
    () =>
      tryBuildReportRequest(
        {
          template: selectedTemplate,
          year,
          quarter,
          month,
          regionType,
          selectedRegion,
        },
        { nationalGeographyLabel },
      ),
    [selectedTemplate, year, quarter, month, regionType, selectedRegion, nationalGeographyLabel],
  );
  const draftRequest = draftResolution.ok ? draftResolution.request : null;
  const draftError = draftResolution.ok ? null : draftResolution.error;

  // The header always describes the request that produced the report on screen.
  const boundRequest = activeRequest ?? (activeRequestUnavailable ? null : draftRequest);
  const headerBinding = useMemo(
    () => (boundRequest ? bindReportHeader(boundRequest, previewMeta) : null),
    [boundRequest, previewMeta],
  );
  const headerConflicts = isSamplePreview ? [] : headerBinding?.conflicts ?? [];
  const unavailableError: ReportRequestError | null = boundRequest
    ? null
    : draftError ?? {
        field: "period",
        code: "period.unrecorded",
        message: "This report does not record a reporting period, so its header cannot be stated.",
      };

  const selectedTemplateLabel =
    templates.find((tpl) => tpl.id === selectedTemplate)?.label ?? "MoH Yearly";
  const parametersSummary = draftRequest?.period ?? "—";
  const geographySummary = draftRequest?.geography ?? "—";
  const exportSummary = `${reportLanguage === "am" ? t("reports.language.am") : t("reports.language.en")} / ${
    exportFormat === "pptx" ? t("reports.format.pptx") : t("reports.format.docx")
  }`;

  const displayReportTitle = headerBinding?.title ?? t("reports.errors.headerUnavailable");
  const displayPeriodText = headerBinding?.periodText ?? t("reports.errors.periodUnavailable");
  const displayGeographyText = headerBinding?.geographyText ?? t("reports.errors.geographyUnavailable");
  const fetchRecentReports = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setRecentReports([]);
      return;
    }
    setRecentLoading(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/reports/recent?limit=3`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;
      const data = (await response.json()) as { reports?: CachedReportSummary[] };
      setRecentReports(Array.isArray(data.reports) ? data.reports : []);
    } catch (err) {
      console.error("Failed to load recent reports:", err);
    } finally {
      setRecentLoading(false);
    }
  }, [apiBaseUrl]);

  const loadCachedReport = useCallback((cached: CachedReportSummary) => {
    const payload = cached.report;
    const sectionsRaw = payload?.sections_with_data;
    if (!Array.isArray(sectionsRaw) || sectionsRaw.length === 0) return;

    const loadedSections: ReportSection[] = sectionsRaw.map((section: unknown) =>
      buildReportSection(section as Record<string, unknown>, undefined, { isStreaming: false }),
    );

    setReportData(loadedSections);
    if (payload?.template) {
      setReportMeta(normalizeReportMeta(payload.template));
    } else {
      setReportMeta({
        title: cached.title,
        geography: cached.geography,
        timePeriod: cached.time_period,
      });
    }
    if (payload?.language === "en" || payload?.language === "am") {
      setReportLanguage(payload.language);
    }
    if (payload?.output_format === "docx" || payload?.output_format === "pptx") {
      setExportFormat(payload.output_format);
    }
    if (typeof payload?.prompt === "string" && payload.prompt.trim()) {
      setUserInstruction(payload.prompt);
    }

    // A restored report is bound to the request that produced it, so the header keeps
    // stating that period and geography instead of whatever the sidebar now holds.
    const storedRequest = reportRequestFromMeta({
      geography: cached.geography,
      timePeriod: cached.time_period,
    });
    setActiveRequest(storedRequest);
    setActiveRequestUnavailable(!storedRequest);
    setGenerationError(null);
    if (storedRequest) {
      setSelectedTemplate(storedRequest.cadence);
      setYear(storedRequest.year);
      if (storedRequest.quarter) setQuarter(storedRequest.quarter);
      if (storedRequest.month) setMonth(storedRequest.month);
      setRegionType(storedRequest.geographyScope);
      if (storedRequest.region) setSelectedRegion(storedRequest.region);
    }
    setActiveRecentId(cached.id);
    setImprovingSectionId(null);
    setImprovementError(null);
  }, []);

  // recent reports are loaded on explicit user action only

  const formatRecentDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const reportCopy = reportLanguage === "am"
    ? {
        period: "ጊዜ",
        geography: "አካባቢ",
        generatingTitle: "ሪፖርት በመፍጠር ላይ...",
        generatingBody: "ክፍሎችን እያዘጋጀ ነው፣ ዳታቤዙን እየጠየቀ እና ጽሑፉን እየጻፈ ነው።",
        source: "ምንጭ: የጤና መረጃ ስርዓት",
        generatedOn: "የተፈጠረበት ቀን",
      }
    : {
        period: "Period",
        geography: "Geography",
        generatingTitle: "Generating report...",
        generatingBody: "Preparing sections, querying the database, and writing the final narrative.",
        source: "Source: Integrated Health Data System",
        generatedOn: "Generated on",
      };

  const cleanPreviewText = (value: string): string => cleanSectionContent(value);

  const previewBullets = (value: string, maxItems = 4, title = ""): string[] => {
    const source = title ? stripLeadingDuplicateTitle(value, title) : value;
    const normalized = cleanSectionContent(source);
    if (!normalized) return [];

    const lineParts = source
      .split(/\r?\n/)
      .map((line) => cleanSectionContent(line.replace(/^[-*•]\s*/, "")))
      .filter(Boolean);

    const candidates = lineParts.length >= 2
      ? lineParts
      : normalized.split(/(?<=[.!?።])\s+/).map((item) => item.trim());

    return candidates
      .filter(Boolean)
      .slice(0, maxItems)
      .map((item) => (item.length > 150 ? `${item.slice(0, 147).trim()}...` : item));
  };

  const renderSectionChart = (section: ReportSection, heightClass = "h-[250px]") => (
    <SectionChart section={section} heightClass={heightClass} />
  );

  const handleGenerate = async () => {
    if (!userInstruction.trim()) {
      return;
    }

    // The resolved draft request is the single source of truth for the report header.
    // When it cannot be resolved the period is unavailable, the banner is already up,
    // and generation is refused instead of falling back to a guessed period.
    const request = draftRequest;
    if (!request) {
      return;
    }

    const monthHint = request.month
      ? ` (Ethiopian month ${request.month}, around ${MONTH_GREGORIAN_EQUIVALENTS[request.month]})`
      : "";
    const prompt = [
      userInstruction.trim(),
      `Requested reporting period: ${request.period} (${request.cadenceLabel} reporting cadence)${monthHint}.`,
      `Requested geography: ${request.geography}${request.region ? ` (${request.region})` : " (national scope)"}.`,
      `Requested language: ${reportLanguage === "am" ? "Amharic" : "English"}.`,
      `Requested output format: ${exportFormat.toUpperCase()}.`,
      `Use exactly this reporting period (${request.period}) and geography (${request.geography}) in the report metadata. Do not substitute another year, quarter, month, or region.`,
      reportLanguage === "am"
        ? "Write the report body only in Amharic. Do not include English duplicate sections, English explanations, or labels like 'English DOCX Output' / 'Amharic DOCX Output'."
        : "Write the report body only in English. Do not include duplicate translated sections or format labels.",
      exportFormat === "pptx"
        ? "Because the selected output is PPTX, provide clean findings only. Do not write slide numbers, slide titles, speaker notes, layout instructions, or text like 'Slide 1'. The app will turn the findings into a real presentation deck."
        : "Because the selected output is DOCX, write each report section as concise report prose.",
      "Return only final report section content. Do not wrap content in markdown code fences, do not say 'Here is the report', and do not include DOCX/PPTX markup. Do not repeat the reporting period, geography, language or output format as lines in the body and do not restate the section title; the app renders those in the report header.",
    ].filter(Boolean).join("\n");

    setGenerationError(null);
    setActiveRequest(request);
    setActiveRequestUnavailable(false);
    setIsGenerating(true);
    setReportData(null);
    setReportMeta(null);
    setActiveRecentId(null);
    setImprovingSectionId(null);
    setImproveInstruction("");
    setImprovementError(null);

    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      const token = getAuthToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`${apiBaseUrl}/api/v1/generate-template-with-data/stream`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          text: prompt,
          language: reportLanguage,
          output_format: exportFormat,
        }),
      });

      if (!response.ok) {
        const detail = await response.text().catch(() => "");
        throw new Error(detail || "Failed to generate report");
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("Streaming not supported in this browser.");
      }

      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let sections: ReportSection[] = [];

      const upsertLocalSection = (
        section: any,
        overrides: Partial<ReportSection> = {}
      ) => {
        const sectionId = String(section?.section_id || section?.section_title || "");
        if (!sectionId) return;

        const existingIndex = sections.findIndex((item) => item.id === sectionId);
        const existing = existingIndex >= 0 ? sections[existingIndex] : undefined;
        const nextSection = buildReportSection(section, existing, overrides);

        if (existingIndex >= 0) {
          sections = sections.map((item, idx) => (idx === existingIndex ? nextSection : item));
        } else {
          sections = [...sections, nextSection];
        }

        setReportData([...sections]);
      };

      const appendLocalChunk = (sectionId: string, chunk: string) => {
        if (!sectionId || !chunk) return;

        const existingIndex = sections.findIndex((item) => item.id === sectionId);
        if (existingIndex < 0) return;

        sections = sections.map((item, idx) =>
          idx === existingIndex
            ? {
                ...item,
                content: `${item.content}${chunk}`,
                isStreaming: true,
              }
            : item
        );

        setReportData([...sections]);
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;
          let event: any;
          try {
            event = JSON.parse(trimmed);
          } catch {
            continue;
          }
          if (event.event === "template" && event.template) {
            setReportMeta(normalizeReportMeta(event.template));
          }
          if (event.event === "section_start" && event.section) {
            upsertLocalSection(event.section, {
              content: "",
              isStreaming: true,
            });
          }
          if (event.event === "section_chunk" && event.section_id) {
            appendLocalChunk(String(event.section_id), String(event.chunk ?? ""));
          }
          if (event.event === "section" && event.section) {
            upsertLocalSection(event.section, {
              isStreaming: false,
            });
          }
          if (event.event === "done" && event.sections_with_data) {
            if (event.output_format === "docx" || event.output_format === "pptx") {
              setExportFormat(event.output_format);
            }
            const finalSections: ReportSection[] = (event.sections_with_data || []).map((section: any) =>
              buildReportSection(
                section,
                sections.find((item) => item.id === String(section.section_id || section.section_title)),
                { isStreaming: false }
              )
            );
            sections = finalSections;
            setReportData(finalSections);
            if (event.template) {
              setReportMeta(normalizeReportMeta(event.template));
            }
            void fetchRecentReports();
          }
        }
      }
    } catch (err) {
      console.error("Report generation failed:", err);
      const detail = err instanceof Error ? err.message.trim() : "";
      setGenerationError(
        detail ? detail.slice(0, 300) : t("reports.errors.generationFailed"),
      );
      setReportData(null);
      setReportMeta(null);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerateSection = async (sectionId: string) => {
    if (!reportData) return;

    const targetSection = reportData.find((section) => section.id === sectionId);
    if (!targetSection) return;

    updateSectionState(sectionId, { isUpdating: true });

    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      const token = getAuthToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`${apiBaseUrl}/api/v1/improve-report-section`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          section_id: targetSection.id,
          section_title: targetSection.title,
          current_content: targetSection.content,
          instruction: "Regenerate this section completely with fresh content while maintaining the same scope and data.",
          language: reportLanguage,
          output_format: exportFormat,
          table_data: targetSection.tableData ?? [],
          current_chart_type: targetSection.chartType,
          current_chart_data: convertToChartJSFormat(targetSection.chartData, targetSection.chartMetricLabel),
        }),
      });

      if (!response.ok) {
        const detail = await response.text().catch(() => "");
        throw new Error(detail || "Failed to regenerate section");
      }

      const result = await response.json();
      const updates: Partial<ReportSection> = {
        content: String(result?.improved_content ?? targetSection.content),
        isUpdating: false,
        isStreaming: false,
      };

      if (result?.improved_chart) {
        updates.chartType = result.improved_chart.type as ReportSection["chartType"];
        updates.chartData = normalizeChartData(result.improved_chart.data);
        updates.chartMetricLabel = normalizeChartMetricLabel(result.improved_chart.data);
      }

      updateSectionState(sectionId, updates);
    } catch (err) {
      console.error("Section regeneration failed:", err);
      updateSectionState(sectionId, { isUpdating: false });
    }
  };

  const updateSectionState = (sectionId: string, updates: Partial<ReportSection>) => {
    setReportData((prev) =>
      prev ? prev.map((section) => (section.id === sectionId ? { ...section, ...updates } : section)) : prev
    );
  };

  const closeImprovePanel = () => {
    setImprovingSectionId(null);
    setImproveInstruction("");
    setImprovementError(null);
  };

  const handleApplyImprovement = async () => {
    if (!improvingSectionId || !reportData || !improveInstruction.trim()) return;

    const targetSection = reportData.find((section) => section.id === improvingSectionId);
    if (!targetSection) return;

    setIsApplyingImprovement(true);
    setImprovementError(null);
    updateSectionState(improvingSectionId, { isUpdating: true });

    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      const token = getAuthToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`${apiBaseUrl}/api/v1/improve-report-section`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          section_id: targetSection.id,
          section_title: targetSection.title,
          current_content: targetSection.content,
          instruction: improveInstruction.trim(),
          language: reportLanguage,
          output_format: exportFormat,
          table_data: targetSection.tableData ?? [],
          current_chart_type: targetSection.chartType,
          current_chart_data: convertToChartJSFormat(targetSection.chartData, targetSection.chartMetricLabel),
        }),
      });

      if (!response.ok) {
        const detail = await response.text().catch(() => "");
        throw new Error(detail || "Failed to improve section");
      }

      const result = await response.json();
      const updates: Partial<ReportSection> = {
        content: String(result?.improved_content ?? targetSection.content),
        isUpdating: false,
        isStreaming: false,
      };

      if (result?.improved_chart) {
        updates.chartType = result.improved_chart.type as ReportSection["chartType"];
        updates.chartData = normalizeChartData(result.improved_chart.data);
        updates.chartMetricLabel = normalizeChartMetricLabel(result.improved_chart.data);
      }

      updateSectionState(improvingSectionId, updates);
      closeImprovePanel();
    } catch (err) {
      console.error("Section improvement failed:", err);
      const message = err instanceof Error ? err.message : "Failed to improve section.";
      setImprovementError(message);
      updateSectionState(improvingSectionId, { isUpdating: false });
    } finally {
      setIsApplyingImprovement(false);
    }
  };

  const currentImprovingSection = reportData?.find(s => s.id === improvingSectionId);

  return (
    <div className="flex flex-col h-screen overflow-hidden text-sm" style={{ background: 'radial-gradient(ellipse at 0% 0%, rgba(59,130,246,0.05) 0%, transparent 50%), radial-gradient(ellipse at 100% 100%, rgba(59,130,246,0.04) 0%, transparent 50%), #f8fafc' }}>
      {(!embedMode || publicMode) && <Navbar publicMode={publicMode} />}
      
      <div className="flex flex-1 overflow-hidden relative">
        {/* LEFT SIDEBAR */}
        <div className={`relative flex-shrink-0 transition-all duration-200 overflow-hidden ${sidebarOpen ? 'w-64' : 'w-0'}`}>
          <aside className="w-64 bg-white/90 border-r border-primary-100/30 overflow-y-auto flex flex-col shadow-sm h-full min-h-0">
            <div className="flex-1 min-h-0 overflow-y-auto p-2.5 space-y-2.5">
              <button
                type="button"
                onClick={() => navigate(publicMode ? "/embed/report-generator/guide" : "/report-generator/guide")}
                className="group flex w-full items-center justify-between rounded-2xl border border-amber-200/70 bg-gradient-to-r from-amber-50 to-white px-3 py-2.5 text-left shadow-sm transition hover:border-amber-300 hover:shadow-md"
              >
                <span>
                  <span className="block text-[10px] font-bold uppercase tracking-[0.12em] text-amber-700">Explore report studio</span>
                  <span className="mt-0.5 block text-xs font-semibold text-slate-800">Want to know more about this page?</span>
                </span>
                <FileText className="h-4 w-4 shrink-0 text-amber-600 transition-transform group-hover:scale-110" />
              </button>
              <SidebarPanel
                title={t("reports.sidebar.templateSelection")}
                summary={selectedTemplateLabel}
                icon={Layers}
                open={sidebarPanels.template}
                onToggle={() => toggleSidebarPanel("template")}
              >
                <div className="space-y-1.5">
                  {templates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => setSelectedTemplate(template.id as TemplateType)}
                      className={cn(
                        "w-full flex items-center justify-between p-2 rounded-xl border transition-all duration-200 text-left",
                        selectedTemplate === template.id
                          ? "border-primary-300 bg-primary-50 text-slate-800"
                          : "border-slate-200 hover:border-primary-200 hover:bg-primary-50/60 text-slate-700",
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <template.icon
                          className={cn(
                            "w-4 h-4",
                            selectedTemplate === template.id ? "text-primary-600" : "text-slate-400",
                          )}
                        />
                        <span className="text-xs font-medium">{template.label}</span>
                      </div>
                      {selectedTemplate === template.id && <ChevronRight className="w-3.5 h-3.5 text-primary-500" />}
                    </button>
                  ))}
                </div>
              </SidebarPanel>

              <SidebarPanel
                title={t("reports.sidebar.parameters")}
                summary={parametersSummary}
                icon={Settings2}
                open={sidebarPanels.parameters}
                onToggle={() => toggleSidebarPanel("parameters")}
              >
                <div className="space-y-2">
                  {selectedTemplate === "monthly" && (
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-600 mb-1">
                        {t("reports.sidebar.month").toUpperCase()}
                      </label>
                      <select
                        className="input h-8 w-full text-xs"
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                      >
                        {months.map((m) => (
                          <option key={m} value={m}>
                            {monthLabel(m)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {selectedTemplate === "quarterly" && (
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-600 mb-1">
                        {t("reports.sidebar.quarter").toUpperCase()}
                      </label>
                      <select
                        className="input h-8 w-full text-xs"
                        value={quarter}
                        onChange={(e) => setQuarter(e.target.value)}
                      >
                        {quarters.map((q) => (
                          <option key={q} value={q}>
                            {q}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-600 mb-1">
                      {t("reports.sidebar.year").toUpperCase()}
                    </label>
                    <select
                      className="input h-8 w-full text-xs"
                      value={year}
                      onChange={(e) => setYear(e.target.value)}
                    >
                      {years.map((y) => (
                        <option key={y} value={y}>
                          {formatEthiopianYear(y)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </SidebarPanel>

              <SidebarPanel
                title={t("reports.sidebar.geography")}
                summary={geographySummary}
                icon={MapPin}
                open={sidebarPanels.geography}
                onToggle={() => toggleSidebarPanel("geography")}
              >
                <div className="space-y-2">
                  <div className="flex gap-1.5 p-0.5 bg-primary-50/60 rounded-xl">
                    <button
                      onClick={() => setRegionType("national")}
                      className={cn(
                        "flex-1 py-1 text-[10px] font-semibold rounded-lg transition-all",
                        regionType === "national"
                          ? "bg-white text-primary-700 shadow-sm"
                          : "text-slate-600 hover:text-primary-600",
                      )}
                    >
                      {t("reports.sidebar.national")}
                    </button>
                    <button
                      onClick={() => setRegionType("regional")}
                      className={cn(
                        "flex-1 py-1 text-[10px] font-semibold rounded-lg transition-all",
                        regionType === "regional"
                          ? "bg-white text-primary-700 shadow-sm"
                          : "text-slate-600 hover:text-primary-600",
                      )}
                    >
                      {t("reports.sidebar.regional")}
                    </button>
                  </div>
                  {regionType === "regional" && (
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-600 mb-1">
                        {t("reports.sidebar.region").toUpperCase()}
                      </label>
                      <select
                        className="input h-8 w-full text-xs"
                        value={selectedRegion}
                        onChange={(e) => setSelectedRegion(e.target.value)}
                      >
                        {regions.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </SidebarPanel>

              {reportData && !isGenerating && (
                <Button
                  onClick={handleDownloadReport}
                  disabled={isExporting || !headerBinding}
                  variant="outline"
                  className="btn-secondary h-10 w-full text-xs"
                >
                  {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                  {exportFormat === "pptx" ? t("reports.download.pptx") : t("reports.download.docx")}
                </Button>
              )}

              <section className="card p-2.5">
                <h3 className="text-[10px] font-semibold text-primary-700 uppercase tracking-[0.14em] mb-2 flex items-center gap-1.5">
                  <History className="w-3.5 h-3.5 text-primary-500" />
                  {t("reports.recent.title")}
                </h3>
                {recentLoading ? (
                  <div className="flex items-center justify-center py-4 text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                ) : recentReports.length === 0 ? (
                  <p className="text-[11px] text-slate-500 leading-relaxed">{t("reports.recent.empty")}</p>
                ) : (
                  <div className="space-y-1.5">
                    {recentReports.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => loadCachedReport(item)}
                        className={cn(
                          "w-full rounded-xl border px-2.5 py-2 text-left transition-all",
                          activeRecentId === item.id
                            ? "border-primary-300 bg-primary-50 shadow-sm"
                            : "border-slate-200 bg-slate-50/80 hover:border-primary-200 hover:bg-white",
                        )}
                      >
                        <p className="text-xs font-semibold text-slate-800 line-clamp-2">{item.title}</p>
                        <p className="mt-0.5 text-[10px] text-slate-500 line-clamp-1">
                          {item.geography || "—"}
                          {item.time_period ? ` · ${item.time_period}` : ""}
                        </p>
                        <div className="mt-1 flex items-center justify-between text-[10px] text-slate-500">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatRecentDate(item.created_at)}
                          </span>
                          <span>
                            {item.section_count} {t("reports.recent.sections")}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </section>
            </div>
          </aside>
        </div>
          <button
            type="button"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="absolute top-2 z-10 flex h-8 w-5 items-center justify-center rounded-3xl border border-primary-300/60 bg-primary-100 text-[9px] text-primary-600 shadow-soft hover:bg-primary-200 hover:text-primary-800 transition-all"
            style={{ left: sidebarOpen ? 'calc(16rem - 10px)' : '-10px' }}
            title={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
          >
            {sidebarOpen ? "◀" : "▶"}
          </button>

        {/* MAIN CONTENT AREA */}
        <main className="flex-1 overflow-y-auto px-3 pt-3 pb-0 flex justify-center transition-all duration-300">
          <div className="w-full max-w-[850px]">
            {(unavailableError || generationError || headerConflicts.length > 0) && (
              <div role="alert" className="mb-3 space-y-2">
                {unavailableError && (
                  <div className="flex items-start gap-2 rounded-2xl border border-red-300 bg-red-50 px-4 py-3 text-xs text-red-800">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      <p className="font-semibold">
                        {unavailableError.field === "geography"
                          ? t("reports.errors.geographyTitle")
                          : t("reports.errors.periodTitle")}
                      </p>
                      <p className="mt-0.5 leading-relaxed">{unavailableError.message}</p>
                    </div>
                  </div>
                )}
                {generationError && (
                  <div className="flex items-start gap-2 rounded-2xl border border-red-300 bg-red-50 px-4 py-3 text-xs text-red-800">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      <p className="font-semibold">{t("reports.errors.generationTitle")}</p>
                      <p className="mt-0.5 leading-relaxed">{generationError}</p>
                    </div>
                  </div>
                )}
                {headerConflicts.length > 0 && (
                  <div className="flex items-start gap-2 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-xs text-amber-900">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      <p className="font-semibold">{t("reports.errors.conflictTitle")}</p>
                      <ul className="mt-1 space-y-0.5">
                        {headerConflicts.map((conflict, index) => (
                          <li key={`${conflict.field}-${index}`}>
                            <span className="font-semibold capitalize">{conflict.field}:</span>{" "}
                            requested <span className="font-semibold">{conflict.requested}</span>,
                            generated report states{" "}
                            <span className="font-semibold">{conflict.generated}</span>. The
                            header shows the requested values.
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* DOCUMENT PREVIEW */}
            <div
              className={cn(
                "group/preview relative bg-white shadow-md rounded-3xl min-h-[900px] w-full p-6 md:p-8 mb-28 border border-primary-100/30",
                "transition-all duration-300 ease-out",
                "hover:shadow-xl hover:shadow-primary-100/50 hover:border-primary-200/60 hover:-translate-y-0.5",
                isSamplePreview && "ring-2 ring-primary-100/80",
              )}
            >
              {isSamplePreview && (
                <div className="absolute top-4 right-4 z-10 flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-2.5 py-1 text-[10px] font-semibold text-primary-700 shadow-sm">
                  <Sparkles className="h-3 w-3" />
                  {reportLanguage === "am" ? "የናሙና ማሳያ" : "Sample preview"}
                </div>
              )}
              {previewSections ? (
                <div className={cn("animate-in fade-in duration-500", isSamplePreview && "opacity-[0.97]")}>
                  {isGenerating && reportData && (
                    <div className="mb-4 flex items-center gap-2 rounded-xl border border-primary-100 bg-primary-50 px-3 py-2 text-xs text-primary-800">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>{reportCopy.generatingBody}</span>
                    </div>
                  )}
                  {/* HEADER */}
                  <div className="text-center mb-8 border-b border-slate-200 pb-5">
                    <h1 className="text-lg font-bold text-slate-900 mb-1 uppercase tracking-tight">
                      {displayReportTitle}
                    </h1>
                    <div className="flex justify-center gap-5 text-xs font-medium text-slate-600 bg-slate-50 py-2 rounded-full w-fit mx-auto px-5">
                      <span className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {displayPeriodText}
                      </span>
                      <span className="flex items-center gap-2 border-l border-slate-200 pl-5">
                        <MapPin className="w-4 h-4" />
                        {displayGeographyText}
                      </span>
                    </div>
                    {!isSamplePreview && (
                      <div className="mt-3 flex justify-center">
                        <Button
                          onClick={handleDownloadReport}
                          disabled={isExporting || !headerBinding}
                          variant="outline"
                          className="btn-secondary h-9 px-4 text-xs"
                        >
                          {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                          {exportFormat === "pptx" ? t("reports.download.pptx") : t("reports.download.docx")}
                        </Button>
                      </div>
                    )}
                  </div>

                  {isSamplePreview && (
                    <div className="mb-6 flex flex-wrap items-center justify-center gap-4 rounded-2xl border border-dashed border-primary-200 bg-gradient-to-r from-primary-50/60 via-white to-emerald-50/40 px-4 py-3 text-center">
                      <div className="flex items-center gap-2 text-xs text-slate-700">
                        <Syringe className="h-4 w-4 text-primary-500" />
                        <span>{reportLanguage === "am" ? "ክትባት" : "Immunization"}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-700">
                        <Baby className="h-4 w-4 text-emerald-600" />
                        <span>{reportLanguage === "am" ? "የእናት ጤና" : "Maternal health"}</span>
                      </div>
                      <p className="w-full text-[11px] text-slate-500 max-w-md">
                        {t("reports.preview.readyDesc")}
                      </p>
                    </div>
                  )}

                  {exportFormat === "pptx" ? (
                    <div className="space-y-5">
                      <section className="aspect-video overflow-hidden rounded-md border border-slate-300 bg-slate-950 text-white shadow-lg">
                        <div className="flex h-full flex-col justify-between bg-[radial-gradient(circle_at_top_right,#1e40af_0,#0f172a_42%,#020617_100%)] p-8">
                          <div>
                            <div className="mb-5 h-1 w-16 bg-primary-400" />
                            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-primary-200">
                              {reportLanguage === "am" ? "የጤና አፈጻጸም ሪፖርት" : "Health Performance Deck"}
                            </p>
                            <h1 className="mt-4 max-w-[78%] text-3xl font-bold leading-tight tracking-normal">
                              {displayReportTitle}
                            </h1>
                          </div>
                          <div className="flex flex-wrap gap-2 text-[11px] font-medium text-primary-100">
                            <span className="rounded border border-white/20 bg-white/10 px-3 py-1">{displayPeriodText}</span>
                            <span className="rounded border border-white/20 bg-white/10 px-3 py-1">{displayGeographyText}</span>
                            <span className="rounded border border-primary-300/40 bg-primary-400/20 px-3 py-1">PPTX</span>
                          </div>
                        </div>
                      </section>

                      <section className="aspect-video overflow-hidden rounded-md border border-slate-300 bg-white shadow-lg">
                        <div className="flex h-full flex-col p-7">
                          <div className="mb-5 flex items-center justify-between border-b border-slate-200 pb-3">
                            <div>
                              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-primary-600">
                                {reportLanguage === "am" ? "አጠቃላይ እይታ" : "Overview"}
                              </p>
                              <h2 className="mt-1 text-xl font-bold text-slate-900">
                                {reportLanguage === "am" ? "የዴኩ አወቃቀር" : "Deck structure"}
                              </h2>
                            </div>
                            <div className="rounded bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">
                              {previewSections.filter((section, idx) => !shouldHideDuplicateReportTitleSection(section.title ?? "", idx)).length} sections
                            </div>
                          </div>
                          <div className="grid flex-1 grid-cols-2 gap-3">
                            {previewSections
                              .filter((section, idx) => !shouldHideDuplicateReportTitleSection(section.title ?? "", idx))
                              .slice(0, 8)
                              .map((section, idx) => (
                                  <div key={`overview-${section.id}`} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                    <p className="text-[11px] font-bold text-primary-600">{String(idx + 1).padStart(2, "0")}</p>
                                  <p className="mt-1 line-clamp-2 text-sm font-semibold text-slate-900">{section.title}</p>
                                </div>
                              ))}
                          </div>
                        </div>
                      </section>

                      {previewSections
                        .filter((section, idx) => !shouldHideDuplicateReportTitleSection(section.title ?? "", idx))
                        .map((section, idx) => {
                            const bullets = previewBullets(section.content, section.chartType ? 4 : 5, section.title);
                          return (
                            <section
                              key={section.id}
                              className="group relative aspect-video overflow-hidden rounded-md border border-slate-300 bg-white shadow-lg scroll-mt-20"
                            >
                              {!isSamplePreview && (
                                <div className="absolute right-3 top-3 z-10 flex gap-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                                  <button
                                    onClick={() => {
                                      setImprovingSectionId(section.id);
                                      setImproveInstruction("");
                                      setImprovementError(null);
                                    }}
                                    disabled={section.isStreaming || section.isUpdating || isApplyingImprovement}
                                    className="flex items-center gap-1.5 rounded-xl border border-primary-200 bg-white px-2 py-1 text-[11px] font-semibold text-primary-700 shadow-sm hover:bg-primary-50 disabled:opacity-50"
                                  >
                                    <Wand2 className="h-3 w-3" />
                                    Improve
                                  </button>
                                  <button
                                    onClick={() => handleRegenerateSection(section.id)}
                                    disabled={section.isUpdating}
                                    className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
                                  >
                                    <RefreshCw className={cn("h-3 w-3", section.isUpdating && "animate-spin")} />
                                    Regenerate
                                  </button>
                                </div>
                              )}

                              {section.isUpdating && (
                                <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/70 backdrop-blur-[1px]">
                                  <div className="flex items-center gap-2 rounded border border-slate-200 bg-white px-4 py-3 text-xs font-semibold text-slate-800 shadow-lg">
                                    <Loader2 className="h-4 w-4 animate-spin text-primary-500" />
                                    Updating section...
                                  </div>
                                </div>
                              )}

                              <div className="flex h-full flex-col p-6">
                                <div className="mb-4 flex items-start justify-between gap-5 border-b border-slate-200 pb-3">
                                  <div>
                                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary-600">
                                      {reportLanguage === "am" ? "ክፍል" : "Section"} {idx + 1}
                                    </p>
                                    <h2 className="mt-1 line-clamp-2 text-xl font-bold leading-tight text-slate-950">
                                      {section.title}
                                    </h2>
                                  </div>
                                  <span className="shrink-0 rounded bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-500">
                                    {String(idx + 3).padStart(2, "0")}
                                  </span>
                                </div>

                                <div className={cn("grid min-h-0 flex-1 gap-5", section.chartType ? "grid-cols-[1.2fr_0.8fr]" : "grid-cols-1")}>
                                  {section.chartType && (
                                    <div ref={section.chartRef} className="min-h-0 rounded border border-slate-200 bg-white p-3">
                                      <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                                        {section.chartMetricLabel ?? "Visualization"}
                                      </p>
                                      {renderSectionChart(section, "h-[270px]")}
                                    </div>
                                  )}
                                  <div className="min-h-0 rounded bg-slate-50 p-4">
                                    <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-primary-600">
                                      {reportLanguage === "am" ? "ዋና ምልከታዎች" : "Key takeaways"}
                                    </p>
                                    <ul className="space-y-2 text-sm leading-snug text-slate-800">
                                      {(bullets.length ? bullets : [cleanPreviewText(section.content)]).filter(Boolean).map((bullet, bulletIdx) => (
                                        <li key={`${section.id}-bullet-${bulletIdx}`} className="flex gap-2">
                                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500" />
                                          <span className="line-clamp-3">{bullet}</span>
                                        </li>
                                      ))}
                                      {section.isStreaming && (
                                        <li className="text-primary-500 animate-pulse">Generating...</li>
                                      )}
                                    </ul>
                                  </div>
                                </div>

                                <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-2 text-[10px] text-slate-500">
                                  <span>{reportCopy.source}</span>
                                  <span>{reportCopy.generatedOn} {new Date().toLocaleDateString()}</span>
                                </div>
                              </div>
                            </section>
                          );
                        })}
                    </div>
                  ) : (
                    <>
                  <div className="space-y-10">
                    {previewSections.map((section, idx) => (
                      <div key={section.id} className="group relative scroll-mt-20">
                        {!isSamplePreview && (
                        <div className="absolute -top-3 -right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 flex gap-2">
                          <button
                            onClick={() => {
                              setImprovingSectionId(section.id);
                              setImproveInstruction("");
                              setImprovementError(null);
                            }}
                            disabled={section.isStreaming || section.isUpdating || isApplyingImprovement}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-white shadow-md border border-primary-100 rounded-full text-xs font-semibold text-primary-600 hover:bg-primary-50 transition-colors disabled:opacity-50"
                          >
                            <Wand2 className="w-3.5 h-3.5" />
                            Improve
                          </button>
                          <button
                            onClick={() => handleRegenerateSection(section.id)}
                            disabled={section.isUpdating}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-white shadow-md border border-slate-200 rounded-full text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
                          >
                            <RefreshCw className={cn("w-3.5 h-3.5", section.isUpdating && "animate-spin")} />
                            Regenerate
                          </button>
                        </div>
                        )}

                        {/* Updating Overlay */}
                        {section.isUpdating && (
                          <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-20 flex flex-col items-center justify-center rounded-xl animate-in fade-in duration-300">
                            <div className="bg-white p-4 rounded-xl shadow-lg border border-slate-200 flex flex-col items-center gap-3">
                              <div className="relative">
                                <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
                                <Sparkles className="absolute -top-1 -right-1 w-4 h-4 text-warning-400 animate-pulse" />
                              </div>
                              <p className="text-xs font-semibold text-slate-800 tracking-tight">Updating section...</p>
                            </div>
                          </div>
                        )}

                        {!shouldHideDuplicateReportTitleSection(section.title ?? "", idx) && (
                          <h2 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2 border-l-2 border-primary-400 pl-3">
                            {previewSections.slice(0, idx + 1).filter((item, itemIdx) => !shouldHideDuplicateReportTitleSection(item.title ?? "", itemIdx)).length}. {section.title}
                          </h2>
                        )}
                        <div className="prose prose-sm max-w-none text-slate-700 leading-relaxed mb-5 whitespace-pre-wrap">
                          {cleanSectionBody(section.content, section.title)}
                          {section.isStreaming && (
                            <span className="ml-1 inline-block text-primary-500 animate-pulse">|</span>
                          )}
                        </div>
                        
                        {section.chartType && (
                          <div ref={section.chartRef} className="bg-white rounded-lg p-4 border border-slate-200 flex flex-col items-center justify-center min-h-[320px] mb-5 shadow-sm">
                            <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.18em] mb-4 self-start">
                              {shouldHideDuplicateReportTitleSection(section.title ?? "", idx)
                                ? "Visualization"
                                : `${section.title} Visualization`}
                            </h4>
                            <SectionChart section={section} heightClass="h-[250px]" />
                            {reportCopy.source ? (
                              <p className="mt-4 text-[10px] text-slate-400 font-medium">
                                {reportCopy.source}
                              </p>
                            ) : null}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* FOOTER */}
                  <div className="mt-20 pt-10 border-t border-slate-100 text-center text-xs text-slate-400 italic">
                    {reportCopy.generatedOn} {new Date().toLocaleDateString()}
                  </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center min-h-[480px] py-24">
                  <div className="w-20 h-20 bg-primary-50 rounded-full flex items-center justify-center mb-5 border border-primary-100/30 shadow-sm">
                    <Loader2 className="w-10 h-10 text-primary-500 animate-spin" />
                  </div>
                  <h3 className="text-base font-semibold text-slate-800">{reportCopy.generatingTitle}</h3>
                  <p className="text-slate-600 mt-2 text-center max-w-sm text-xs">{reportCopy.generatingBody}</p>
                </div>
              )}
            </div>
            <section className="sticky bottom-0 z-40 -mx-3 border-t border-primary-100/30 bg-gradient-to-t from-slate-50 via-slate-50/95 to-slate-50/75 px-3 py-3 backdrop-blur-sm">
              <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div className="px-1">
                  <h3 className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-primary-700">
                    <MessageSquare className="w-3.5 h-3.5 text-primary-500" />
                    {t("reports.sidebar.aiGuidance")}
                  </h3>
                  <p className="mt-0.5 text-[10px] text-slate-500">{exportSummary}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                    {t("reports.form.language")}
                    <select
                      className="input h-9 w-28 text-[10px]"
                      value={reportLanguage}
                      onChange={(e) => setReportLanguage(e.target.value as "en" | "am")}
                    >
                      <option value="en">{t("reports.language.en")}</option>
                      <option value="am">{t("reports.language.am")}</option>
                    </select>
                  </label>
                  <label className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                    {t("reports.form.format")}
                    <select
                      className="input h-9 w-36 text-[10px]"
                      value={exportFormat}
                      onChange={(e) => setExportFormat(e.target.value as "docx" | "pptx")}
                    >
                      <option value="docx">{t("reports.format.docx")}</option>
                      <option value="pptx">{t("reports.format.pptx")}</option>
                    </select>
                  </label>
                </div>
              </div>
              <div className="relative bg-white rounded-3xl shadow-sm border border-primary-100/30 backdrop-blur-sm focus-within:ring-2 focus-within:ring-primary-200">
                <div className="flex items-end gap-2 p-2.5">
                  <textarea
                    aria-label="AI guidance"
                    rows={2}
                    className="min-h-[42px] max-h-28 flex-1 resize-none border-0 bg-transparent px-0 py-1 text-[13px] leading-5 text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-0 disabled:opacity-50"
                    placeholder="e.g. Focus on immunization, ANC 4+ visits, and maternal health by region..."
                    value={userInstruction}
                    onChange={(e) => setUserInstruction(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        if (!isGenerating && userInstruction.trim()) {
                          void handleGenerate();
                        }
                      }
                    }}
                    disabled={isGenerating}
                  />
                  <Button
                    onClick={handleGenerate}
                    disabled={isGenerating || !userInstruction.trim()}
                    className={cn(
                      "flex-shrink-0 h-9 px-3 rounded-md text-xs font-semibold transition-all duration-200 active:scale-[0.98]",
                      userInstruction.trim() && !isGenerating
                        ? "bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white shadow-lg hover:shadow-xl"
                        : "bg-slate-200 text-slate-500",
                    )}
                    title={t("reports.form.generate")}
                  >
                    {isGenerating ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Send className="w-3.5 h-3.5" />
                    )}
                    <span className="hidden sm:inline">
                      {isGenerating ? "Generating..." : t("reports.form.generate")}
                    </span>
                  </Button>
                </div>
              </div>
            </section>
          </div>
        </main>

        {/* IMPROVE RIGHT PANEL */}
        <aside className={cn(
          "fixed right-0 top-0 bottom-0 w-[400px] bg-white border-l border-slate-200 shadow-2xl z-[60] transform transition-transform duration-300 ease-in-out",
          improvingSectionId ? "translate-x-0" : "translate-x-full"
        )}>
          {currentImprovingSection && (
            <div className="h-full flex flex-col">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-primary-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-200">
                    <Wand2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 leading-tight">Improve Section</h3>
                    <p className="text-xs text-primary-600 font-medium">Refining: {currentImprovingSection.title}</p>
                  </div>
                </div>
                <button 
                  onClick={closeImprovePanel}
                  className="p-2 hover:bg-white rounded-full transition-colors text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">CURRENT CONTENT PREVIEW</h4>
                  <div className="text-sm text-slate-600 line-clamp-4 italic leading-relaxed">
                    "{currentImprovingSection.content}"
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-bold text-slate-900 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-primary-600" />
                    How should AI improve this?
                  </label>
                  <textarea
                    autoFocus
                    className="w-full p-4 rounded-2xl border-2 border-slate-100 bg-white text-sm focus:border-primary-500 outline-none transition-all resize-none h-48 placeholder:text-slate-300 leading-relaxed"
                    placeholder="e.g. Make it more professional, focus on the regional disparities, or add a summary of the next steps..."
                    value={improveInstruction}
                    disabled={isApplyingImprovement}
                    onChange={(e) => setImproveInstruction(e.target.value)}
                  />
                </div>

                <div className="bg-primary-50 rounded-xl p-4 flex gap-3">
                  <Sparkles className="w-5 h-5 text-primary-500 shrink-0" />
                  <p className="text-xs text-primary-700 leading-normal">
                    AI will analyze the current content, your instructions, and the section's database rows to rewrite this section without changing its factual grounding.
                  </p>
                </div>

                {improvementError && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                    {improvementError}
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-slate-100 bg-white shadow-[-4px_-4px_20px_rgba(0,0,0,0.02)]">
                <Button
                  onClick={handleApplyImprovement}
                  disabled={isApplyingImprovement || !improveInstruction.trim()}
                  className="w-full h-12 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl shadow-lg transition-all active:scale-[0.98] disabled:opacity-50 group"
                >
                  {isApplyingImprovement ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Applying AI Changes...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
                      Apply AI Improvement
                    </div>
                  )}
                </Button>
                <p className="text-[10px] text-center text-slate-400 mt-4">
                  Only the <strong>{currentImprovingSection.title}</strong> section will be modified.
                </p>
              </div>
            </div>
          )}
        </aside>

        {/* Backdrop for Improve Panel */}
        {improvingSectionId && (
          <div 
            className="fixed inset-0 bg-slate-900/10 backdrop-blur-[2px] z-[55] animate-in fade-in duration-300"
            onClick={() => !isApplyingImprovement && setImprovingSectionId(null)}
          />
        )}
      </div>
    </div>
  );
};
